import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { alpha, Box, Chip, IconButton, Typography, useTheme } from '@mui/material';
import { ChevronRightFilled, EyeRegular, Warning12Regular } from '@fluentui/react-icons';
import BadgeIcon from '@public/assets/images/badge-icon.png';
import useIncidentTileStyles from './useIncidentTileStyles';
import ImagePreviewDialog from './components/ImagePreviewDialog';
import RadioOffIcon from '../icons/radio-off';
import RadioOnIcon from '../icons/radio-on';
import ClockIcon from '../icons/clock';
import WarningIcon from '../icons/warning';
import QuestionIcon from '../icons/question';
import PersonGroupIcon from '../icons/person-group';
import CarIcon from '../icons/car';
const IncidentTile = ({ imgSrc, title, incidentId, date, time, venue, isRead = true, legend, index, incidentCount, arrow, showImagePreviewIcon = true, padding, shadowed = false, borderBottom = true, curved = false, score, onTileClick, onArrowClick, onAlertClick, variant = 'default' }) => {
    const theme = useTheme();
    const { eyeStyles, tileContentContainerStyles, imageContainerStyles, imageStyles, tileStyles, incidentCountChipStyles, alertIconStyles } = useIncidentTileStyles(incidentCount);
    const legendIconMap = {
        Neutral: _jsx(RadioOnIcon, {}),
        Traffic: _jsx(RadioOffIcon, {}),
        Time: _jsx(ClockIcon, {}),
        Suspicious: _jsx(WarningIcon, {}),
        Behaviour: _jsx(QuestionIcon, {}),
        Criminal: _jsx(PersonGroupIcon, {}),
        VehicleType: _jsx(CarIcon, {})
    };
    const [previewImage, setPreviewImage] = useState('');
    const [showPreviewDialog, setShowPreviewDialog] = useState(false);
    const handleOpenImagePreviewDialog = (imgSrc) => {
        setShowPreviewDialog((curr) => (curr = true));
        setPreviewImage(imgSrc);
    };
    const handleClick = () => {
        if (incidentId && onAlertClick) {
            onAlertClick(incidentId);
        }
        else {
            if (onTileClick)
                onTileClick(title);
        }
    };
    const handleCloseImagePreviewDialog = () => {
        setShowPreviewDialog((curr) => (curr = false));
        setPreviewImage('');
    };
    const getAlertColor = (score) => {
        if (score === undefined)
            return theme.palette.grey[400];
        if (score <= 50)
            return '#FFAB00';
        if (score > 50 && score < 75)
            return '#FF8F44';
        if (score >= 75)
            return '#B71D18';
    };
    const icon = legendIconMap[legend] ?? _jsx(Warning12Regular, {});
    return (_jsxs(Box, { sx: {
            ...tileStyles,
            background: (theme) => (!isRead ? theme.palette.background.neutral : 'unset'),
            boxShadow: (theme) => (shadowed ? theme.customShadows.z4 : undefined),
            borderBottom: (theme) => (borderBottom ? `1px solid ${theme.palette.divider}` : undefined),
            borderRadius: curved ? 1 : undefined,
            padding: padding
        }, className: 'incident-tile', children: [index && (_jsx(Typography, { variant: 'h6', color: 'textPrimary', children: index })), _jsxs(Box, { sx: imageContainerStyles, onClick: () => showImagePreviewIcon && handleOpenImagePreviewDialog(imgSrc), children: [imgSrc ? (_jsx(_Fragment, { children: _jsx("img", { src: imgSrc, style: imageStyles }) })) : (_jsx(_Fragment, { children: _jsx(Box, { sx: {
                                ...imageContainerStyles,
                                backgroundColor: theme.palette.action.disabled,
                                justifyContent: 'center',
                                alignItems: 'center',
                                display: 'flex',
                                padding: '5px'
                            }, children: _jsx(Typography, { color: 'white', fontSize: 7, children: "No preview avaialable" }) }) })), showImagePreviewIcon && _jsx(EyeRegular, { fontSize: 20, color: 'white', style: eyeStyles })] }), _jsxs(Box, { className: 'tile-content-container', sx: tileContentContainerStyles, onClick: () => handleClick(), children: [_jsx(Typography, { variant: 'body1', color: 'textPrimary', children: title }), _jsxs(Typography, { variant: 'caption', color: 'textDisabled', children: [date, " ", time, " ", venue ? _jsxs(_Fragment, { children: ["\u2022 ", venue] }) : ''] })] }), variant === 'default' ? (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1.5, children: [!isRead && (_jsx(Box, { className: 'badge', display: 'flex', alignItems: 'center', justifyContent: 'center', children: _jsx("img", { src: BadgeIcon, alt: 'unread-badge', width: 10, height: 10 }) })), incidentCount !== undefined && incidentCount !== null && (_jsx(Chip, { className: 'incident-count-chip', size: 'medium', sx: incidentCountChipStyles, label: incidentCount })), arrow && (_jsx(IconButton, { onClick: () => onArrowClick && onArrowClick(title), children: _jsx(ChevronRightFilled, { fontSize: 24, color: theme.palette.text.primary }) }))] })) : (_jsxs(Box, { sx: {
                    position: 'relative'
                }, children: [_jsxs(Box, { sx: {
                            ...alertIconStyles,
                            backgroundColor: getAlertColor(score)
                        }, children: [icon, _jsx(Typography, { variant: 'subtitle2', fontWeight: 500, color: 'white', children: score })] }), !isRead && (_jsx(Box, { sx: {
                            position: 'absolute',
                            top: -8,
                            right: -10,
                            width: 8,
                            height: 8,
                            backgroundColor: alpha(theme.palette.info.main, 0.48),
                            borderRadius: '50%'
                        } }))] })), !!showPreviewDialog && (_jsx(ImagePreviewDialog, { open: showPreviewDialog, onClose: handleCloseImagePreviewDialog, imgSrc: previewImage, alt: 'incident-preview' }))] }));
};
export default IncidentTile;
